import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SkeletonComponent } from '@components/skeleton/skeleton.component';

/**
 * Supported card variants:
 * - `stat`    — compact metric card (label + value row).
 * - `feature` — large action card (icon, text, optional body block, button).
 * - `detail`  — horizontal card with image left + content right (title, badge, meta, description, tier sub-cards).
 * - `cta`     — pill-shaped button placeholder.
 */
export type CardGridSkeletonCardVariant = 'stat' | 'feature' | 'detail' | 'cta' | 'ticket-purchased';

export type CardGridSkeletonFeatureLayout = 'media' | 'text';

export interface CardGridSkeletonRow {
  cards: number;
  variant: CardGridSkeletonCardVariant;
  featureLayouts?: CardGridSkeletonFeatureLayout[];
  detailTierCount?: number;
}

interface ResolvedRow extends CardGridSkeletonRow {
  cardIndices: number[];
  tierIndices: number[];
}

const DEFAULT_ROWS: CardGridSkeletonRow[] = [
  { cards: 3, variant: 'stat' },
  {
    cards: 2,
    variant: 'feature',
    featureLayouts: ['media', 'text']
  }
];

@Component({
  selector: 'app-card-grid-skeleton',
  templateUrl: './card-grid-skeleton.component.html',
  styleUrls: ['./card-grid-skeleton.component.scss'],
  standalone: true,
  imports: [CommonModule, SkeletonComponent]
})
export class CardGridSkeletonComponent {
  @Input() showHeadingSkeleton = false;

  resolvedRows: ResolvedRow[] = this.buildRows(null);

  @Input() set rows(value: CardGridSkeletonRow[] | null) {
    this.resolvedRows = this.buildRows(value);
  }

  featureLayout(
    row: CardGridSkeletonRow,
    index: number
  ): CardGridSkeletonFeatureLayout {
    const layouts = row.featureLayouts;
    if (layouts && layouts.length === row.cards && index < layouts.length) {
      return layouts[index];
    }
    return 'media';
  }

  private buildRows(input: CardGridSkeletonRow[] | null): ResolvedRow[] {
    const rows = input?.length ? input : DEFAULT_ROWS;
    return rows.map(row => ({
      ...row,
      cardIndices: Array.from({ length: Math.max(0, row.cards) }, (_, i) => i),
      tierIndices: Array.from(
        { length: Math.max(0, row.detailTierCount ?? 3) },
        (_, i) => i
      ),
    }));
  }
}
